/**
 * Snapshot Diff Module
 *
 * Compares two accessibility snapshots to generate a semantic diff
 * showing what changed after a browser action. Used by action tools
 * (click, type, press_key, etc.) to provide focused feedback without
 * requiring a full snapshot.
 *
 * @module
 * @see browserTools.ts - Action tools that use this module
 */

import { parseSnapshot, type SnapshotElement } from './snapshot-analyzer.js';
import {
  compareEditingContexts,
  extractEditingContextsFromElements,
  type EditingContext,
  type EditingContextChange,
} from './snapshot-editing-context.js';
import { findActiveElement, buildSectionTree, type Section } from './snapshot-formatter.js';

/**
 * State of a single element for comparison.
 */
export interface ElementState {
  ref: string;
  role: string;
  text: string;
  inputValue?: string;
  isActive: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  isChecked: boolean;
  isExpanded: boolean;
}

/**
 * A change to an element's state property.
 */
export interface ElementStateChange {
  ref: string;
  role: string;
  text: string;
  field: 'disabled' | 'selected' | 'checked' | 'expanded' | 'inputValue';
  before: string | boolean;
  after: string | boolean;
}

/**
 * Represents a captured snapshot state for comparison.
 */
export interface SnapshotState {
  url: string;
  yaml: string;
  elements: SnapshotElement[];
  activeElement: SnapshotElement | null;
  dialogStack: string[];
  editingContexts: EditingContext[];
  elementsByRef: Map<string, ElementState>;
  interactiveRefs: Set<string>;
  /**
   * Text from non-interactive informational roles (paragraphs, headings,
   * descriptive text). Lets the diff catch content the interactive-ref diff is
   * blind to — e.g. a validation message rendered as plain text.
   */
  contentTexts: Set<string>;
  /**
   * Text from live-region roles (`alert`/`status`) specifically — high-signal
   * for errors/announcements, surfaced even when nothing structural changed.
   */
  alertTexts: Set<string>;
}

/**
 * Info about in-flight network requests at the time of snapshot capture.
 */
export interface PendingNetworkInfo {
  pendingCount: number;
  sampleUrls: string[];
}

/**
 * The semantic diff result comparing before and after states.
 */
export interface SemanticDiff {
  urlChange: { before: string; after: string; navigated: boolean } | null;
  activeChange: { before: ElementState | null; after: ElementState | null } | null;
  dialogStackChange: { before: string[]; after: string[] } | null;
  newElements: ElementState[];
  removedElements: ElementState[];
  stateChanges: ElementStateChange[];
  editingContextChanges: EditingContextChange[];
  /**
   * Non-interactive informational text that appeared/disappeared, plus newly
   * surfaced live-region (`alert`/`status`) text. Null when no content changed.
   */
  contentChange: {
    addedTexts: string[];
    removedTexts: string[];
    newAlerts: string[];
  } | null;
  elementCountDelta: number;
}

// Interactive roles that we track for new/removed element reporting
const INTERACTIVE_ROLES = new Set([
  'button',
  'link',
  'textbox',
  'checkbox',
  'radio',
  'combobox',
  'option',
  'menuitem',
  'tab',
  'slider',
  'spinbutton',
  'searchbox',
  'switch',
  'menu',
  'menubar',
  'tablist',
  'listbox',
]);

// Dialog roles for tracking dialog stack
const DIALOG_ROLES = new Set(['dialog', 'alertdialog']);

// Non-interactive informational roles whose text we track for content diffs.
const CONTENT_ROLES = new Set([
  'text',
  'paragraph',
  'heading',
  'alert',
  'status',
  'note',
]);

// Live-region roles whose appearance is high-signal (errors / announcements).
const ALERT_ROLES = new Set(['alert', 'status']);

/**
 * Extract element state for comparison.
 */
function extractElementState(el: SnapshotElement): ElementState {
  return {
    ref: el.ref,
    role: el.role,
    text: el.text || '',
    inputValue: el.inputValue,
    isActive: el.attributes['active'] !== undefined || el.rawLine.includes('[active]'),
    isDisabled: el.attributes['disabled'] !== undefined || el.rawLine.includes('[disabled]'),
    isSelected: el.attributes['selected'] === 'true',
    isChecked: el.attributes['checked'] === 'true',
    isExpanded: el.attributes['expanded'] === 'true',
  };
}

/**
 * Extract dialog stack from sections (focused dialog first, then background dialogs).
 */
function extractDialogStack(sections: Section[]): string[] {
  const dialogs: string[] = [];

  function findDialogs(sectionList: Section[]): void {
    for (const section of sectionList) {
      if (DIALOG_ROLES.has(section.role)) {
        // Focused dialog (contains active element) goes first
        if (section.containsActive) {
          dialogs.unshift(section.heading);
        } else {
          dialogs.push(section.heading);
        }
      }
      findDialogs(section.subsections);
    }
  }

  findDialogs(sections);
  return dialogs;
}

/**
 * Capture the current state of a page for later comparison.
 *
 * @param url - Current page URL
 * @param yaml - Raw YAML accessibility tree
 * @returns Captured state for diff comparison
 */
export function captureSnapshotState(url: string, yaml: string): SnapshotState {
  const elements = parseSnapshot(yaml);
  const activeElement = findActiveElement(elements);
  const sections = buildSectionTree(elements);
  const dialogStack = extractDialogStack(sections);
  const editingContexts = extractEditingContextsFromElements(elements);

  // Build element state map for comparison
  const elementsByRef = new Map<string, ElementState>();
  const interactiveRefs = new Set<string>();
  const contentTexts = new Set<string>();
  const alertTexts = new Set<string>();

  function traverse(el: SnapshotElement): void {
    if (el.ref) {
      const state = extractElementState(el);
      elementsByRef.set(el.ref, state);
      if (INTERACTIVE_ROLES.has(el.role)) {
        interactiveRefs.add(el.ref);
      }
    }
    const text = el.text?.trim();
    if (text && CONTENT_ROLES.has(el.role)) {
      contentTexts.add(text);
      if (ALERT_ROLES.has(el.role)) {
        alertTexts.add(text);
      }
    }
    for (const child of el.children) {
      traverse(child);
    }
  }

  for (const el of elements) {
    traverse(el);
  }

  return {
    url,
    yaml,
    elements,
    activeElement,
    dialogStack,
    editingContexts,
    elementsByRef,
    interactiveRefs,
    contentTexts,
    alertTexts,
  };
}

/** Items present in `after` but not `before`. */
function added(before: Set<string>, after: Set<string>): string[] {
  const out: string[] = [];
  for (const t of after) if (!before.has(t)) out.push(t);
  return out;
}

/**
 * Compare two snapshot states and generate a semantic diff.
 *
 * @param before - State before the action
 * @param after - State after the action
 * @returns Semantic diff showing what changed
 */
export function compareSnapshots(before: SnapshotState, after: SnapshotState): SemanticDiff {
  // URL change detection
  const urlChange =
    before.url !== after.url ? { before: before.url, after: after.url, navigated: true } : null;

  // Active element change detection
  const beforeActive = before.activeElement ? extractElementState(before.activeElement) : null;
  const afterActive = after.activeElement ? extractElementState(after.activeElement) : null;

  const activeChange =
    beforeActive?.ref !== afterActive?.ref ? { before: beforeActive, after: afterActive } : null;

  // Dialog stack change detection
  const dialogsChanged = JSON.stringify(before.dialogStack) !== JSON.stringify(after.dialogStack);
  const dialogStackChange = dialogsChanged
    ? { before: before.dialogStack, after: after.dialogStack }
    : null;

  // New and removed interactive elements
  const newElements: ElementState[] = [];
  const removedElements: ElementState[] = [];

  for (const ref of after.interactiveRefs) {
    if (!before.interactiveRefs.has(ref)) {
      const state = after.elementsByRef.get(ref);
      if (state) {
        newElements.push(state);
      }
    }
  }

  for (const ref of before.interactiveRefs) {
    if (!after.interactiveRefs.has(ref)) {
      const state = before.elementsByRef.get(ref);
      if (state) {
        removedElements.push(state);
      }
    }
  }

  // State changes for elements that exist in both
  const stateChanges: ElementStateChange[] = [];

  for (const [ref, beforeState] of before.elementsByRef) {
    const afterState = after.elementsByRef.get(ref);
    if (!afterState) continue;

    // Check disabled state change
    if (beforeState.isDisabled !== afterState.isDisabled) {
      stateChanges.push({
        ref,
        role: afterState.role,
        text: afterState.text,
        field: 'disabled',
        before: beforeState.isDisabled,
        after: afterState.isDisabled,
      });
    }

    // Check selected state change
    if (beforeState.isSelected !== afterState.isSelected) {
      stateChanges.push({
        ref,
        role: afterState.role,
        text: afterState.text,
        field: 'selected',
        before: beforeState.isSelected,
        after: afterState.isSelected,
      });
    }

    // Check checked state change
    if (beforeState.isChecked !== afterState.isChecked) {
      stateChanges.push({
        ref,
        role: afterState.role,
        text: afterState.text,
        field: 'checked',
        before: beforeState.isChecked,
        after: afterState.isChecked,
      });
    }

    // Check expanded state change
    if (beforeState.isExpanded !== afterState.isExpanded) {
      stateChanges.push({
        ref,
        role: afterState.role,
        text: afterState.text,
        field: 'expanded',
        before: beforeState.isExpanded,
        after: afterState.isExpanded,
      });
    }

    // Check input value change
    if (beforeState.inputValue !== afterState.inputValue) {
      stateChanges.push({
        ref,
        role: afterState.role,
        text: afterState.text,
        field: 'inputValue',
        before: beforeState.inputValue || '',
        after: afterState.inputValue || '',
      });
    }
  }

  // Element count delta
  const elementCountDelta = after.elementsByRef.size - before.elementsByRef.size;
  const editingContextChanges = compareEditingContexts(
    before.editingContexts,
    after.editingContexts
  );

  // Non-interactive content changes (new/removed informational text, new alerts)
  const addedTexts = added(before.contentTexts, after.contentTexts);
  const removedTexts = added(after.contentTexts, before.contentTexts);
  const newAlerts = added(before.alertTexts, after.alertTexts);
  const contentChange =
    addedTexts.length > 0 || removedTexts.length > 0 || newAlerts.length > 0
      ? { addedTexts, removedTexts, newAlerts }
      : null;

  return {
    urlChange,
    activeChange,
    dialogStackChange,
    newElements,
    removedElements,
    stateChanges,
    editingContextChanges,
    contentChange,
    elementCountDelta,
  };
}

/**
 * Classify whether a diff warrants auto-attaching a screenshot, vs. a
 * text-only diff. Returns `warranted: true` for changes that visually reorient
 * the agent — navigation, a dialog opening/closing/reordering, a large
 * structural shift, newly surfaced alert/text content, or a bulk state change.
 * Returns `false` for the token-burn cases (input-value-only, focus-only, a
 * single state toggle) where the text diff already says everything.
 *
 * Deliberately NOT warranted: a bare `activeChange` (focus moving to a real
 * target). Focus moves on essentially every click into a field, so warranting
 * there would re-attach a screenshot on most actions and defeat the whole point
 * of the conditional-screenshot path. Focus-only is the canonical withhold case.
 *
 * Thresholds are intentionally generous — the cost of an unwarranted screenshot
 * is tokens; the cost of a missing one is the agent calling browser_screenshot.
 */
export function classifyScreenshotWorthiness(
  diff: SemanticDiff,
  opts?: { structuralThreshold?: number; contentThreshold?: number; stateThreshold?: number }
): { warranted: boolean; reason?: string } {
  const structuralThreshold = opts?.structuralThreshold ?? 8;
  const contentThreshold = opts?.contentThreshold ?? 3;
  const stateThreshold = opts?.stateThreshold ?? 5;

  if (diff.urlChange?.navigated) {
    return { warranted: true, reason: 'navigation' };
  }
  if (diff.dialogStackChange) {
    // A dialog opened, closed, or reordered — all visually reorient the agent.
    // (Previously only an *open* warranted one; a close reveals the page beneath
    // and is just as orienting.)
    const opened =
      diff.dialogStackChange.after.length > diff.dialogStackChange.before.length;
    return { warranted: true, reason: opened ? 'dialog-opened' : 'dialog-changed' };
  }
  const structuralDelta = diff.newElements.length + diff.removedElements.length;
  if (structuralDelta > structuralThreshold) {
    return { warranted: true, reason: 'structural-change' };
  }
  if (diff.contentChange) {
    if (diff.contentChange.newAlerts.length > 0) {
      return { warranted: true, reason: 'new-alert' };
    }
    if (diff.contentChange.addedTexts.length > contentThreshold) {
      return { warranted: true, reason: 'new-content' };
    }
  }
  // A bulk state shift (many toggles/selects/checks at once) is visually
  // meaningful; a single input/focus/toggle is not (that's the withhold case).
  if (diff.stateChanges.length > stateThreshold) {
    return { warranted: true, reason: 'state-change' };
  }
  return { warranted: false };
}

/**
 * Check if a diff has any significant changes worth reporting.
 */
export function hasDiffChanges(diff: SemanticDiff): boolean {
  return (
    diff.urlChange !== null ||
    diff.activeChange !== null ||
    diff.dialogStackChange !== null ||
    diff.newElements.length > 0 ||
    diff.removedElements.length > 0 ||
    diff.stateChanges.length > 0 ||
    diff.editingContextChanges.length > 0 ||
    diff.contentChange !== null
  );
}

/**
 * Format element state for display.
 */
function formatElementBrief(el: ElementState | null): string {
  if (!el) return 'none';
  const text = el.text ? ` "${truncate(el.text, 30)}"` : '';
  return `[${el.ref}] ${el.role}${text}`;
}

/**
 * Truncate text to max length with ellipsis.
 */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '...';
}

/**
 * Generate a contextual hint based on the diff.
 * Hints are prescriptive - they tell the agent what to do next.
 */
function generateHint(diff: SemanticDiff, networkInfo?: PendingNetworkInfo): string {
  const hints: string[] = [];

  // Navigation is the most significant change - refs are all stale
  if (diff.urlChange?.navigated) {
    hints.push('Navigation occurred. Take browser_snapshot for fresh refs before proceeding.');
    return hints.join(' ');
  }

  // Dialog opening is high priority - agent should focus on dialog content
  if (
    diff.dialogStackChange &&
    diff.dialogStackChange.after.length > diff.dialogStackChange.before.length
  ) {
    const newDialog = diff.dialogStackChange.after[0];
    hints.push(`Modal "${newDialog}" opened. Focus on dialog content for next action.`);
  }

  // Dialog closing - may reveal new content
  if (
    diff.dialogStackChange &&
    diff.dialogStackChange.after.length < diff.dialogStackChange.before.length
  ) {
    hints.push('Dialog closed. Background content now accessible.');
  }

  // Active element change suggests where to focus
  if (diff.activeChange?.after) {
    const active = diff.activeChange.after;
    if (active.role === 'textbox' || active.role === 'searchbox' || active.role === 'combobox') {
      hints.push(
        `Focus is on ${active.role} "${truncate(active.text, 20)}". Ready for browser_type.`
      );
    } else if (active.role === 'button' || active.role === 'link') {
      hints.push(
        `Focus is on ${active.role} "${truncate(active.text, 20)}". Press Enter to activate.`
      );
    }
  }

  const blocker = diff.editingContextChanges.find(
    (change) => change.kind === 'blocker-added' || change.kind === 'save-blocked'
  );
  if (blocker) {
    hints.push(`${blocker.message} Fill required fields before saving.`);
  }

  const submitEnabled = diff.editingContextChanges.find(
    (change) => change.kind === 'submit-enabled'
  );
  if (submitEnabled) {
    hints.push(`${submitEnabled.message} Saving should now be available.`);
  }

  const contextAppeared = diff.editingContextChanges.find(
    (change) => change.kind === 'context-appeared'
  );
  if (contextAppeared) {
    hints.push(`${contextAppeared.message} Use the EDITING CONTEXT fields for the next action.`);
  }

  // State changes may indicate form validation or permission issues
  const disabledChanges = diff.stateChanges.filter(
    (c) => c.field === 'disabled' && c.after === true
  );
  if (disabledChanges.length > 0) {
    hints.push('Element became disabled. Check for validation errors or permission changes.');
  }

  // Significant content removal may indicate page state change
  if (diff.removedElements.length > 10) {
    hints.push('Significant content removed. Verify page is in expected state.');
  }

  // Significant content addition may indicate new content loaded
  if (diff.newElements.length > 10) {
    hints.push('New content appeared. May need browser_snapshot for full context.');
  }

  // Default hint if nothing specific detected
  if (hints.length === 0) {
    if (hasDiffChanges(diff)) {
      if (networkInfo && networkInfo.pendingCount > 0) {
        hints.push(
          `Changes detected but page is still loading (${networkInfo.pendingCount} pending request${networkInfo.pendingCount === 1 ? '' : 's'}). Take browser_snapshot when ready.`
        );
      } else {
        hints.push(
          'Changes detected. Proceed with next action or use browser_snapshot for full context.'
        );
      }
    } else if (networkInfo && networkInfo.pendingCount > 0) {
      hints.push(
        'No visible changes yet but network requests are in progress. The page is likely still loading — wait briefly then take browser_snapshot.'
      );
    } else {
      hints.push('No changes detected. Verify the intended element was targeted.');
    }
  }

  return hints.join(' ');
}

/**
 * Format a semantic diff for display to the agent.
 *
 * @param diff - The semantic diff to format
 * @param actionDescription - Description of the action that was performed
 * @returns Formatted diff string for the agent
 */
export function formatSemanticDiff(
  diff: SemanticDiff,
  actionDescription: string,
  networkInfo?: PendingNetworkInfo,
  currentTime?: string
): string {
  const lines: string[] = [];

  lines.push('---');
  lines.push('SEMANTIC DIFF: Page state changes after browser action.');
  lines.push(`BROWSER TIME: ${currentTime ?? new Date().toISOString()} (UTC)`);
  lines.push('Format: [PREVIOUS] value [CURRENT] value');
  lines.push('');
  lines.push(`ACTION: ${actionDescription}`);

  // URL line
  if (diff.urlChange) {
    lines.push(
      `URL: [PREVIOUS] ${diff.urlChange.before} [CURRENT] ${diff.urlChange.after} (NAVIGATED)`
    );
  }

  // Active element line
  if (diff.activeChange) {
    const before = formatElementBrief(diff.activeChange.before);
    const after = formatElementBrief(diff.activeChange.after);
    lines.push(`ACTIVE: [PREVIOUS] ${before} [CURRENT] ${after}`);
  }

  // Dialog stack line
  if (diff.dialogStackChange) {
    const before =
      diff.dialogStackChange.before.length > 0
        ? diff.dialogStackChange.before.join(' -> ')
        : '(none)';
    const after =
      diff.dialogStackChange.after.length > 0
        ? diff.dialogStackChange.after.join(' -> ')
        : '(none)';
    lines.push(`DIALOGS: [PREVIOUS] ${before} [CURRENT] ${after}`);
  }

  // Changes section
  const hasChanges =
    diff.newElements.length > 0 ||
    diff.removedElements.length > 0 ||
    diff.stateChanges.length > 0 ||
    diff.editingContextChanges.length > 0 ||
    diff.contentChange !== null;

  if (hasChanges) {
    lines.push('');
    lines.push('CHANGES DETECTED:');

    // New dialogs (most important)
    if (diff.dialogStackChange) {
      const newDialogs = diff.dialogStackChange.after.filter(
        (d) => !diff.dialogStackChange!.before.includes(d)
      );
      for (const dialog of newDialogs) {
        lines.push(`  - NEW DIALOG: "${dialog}"`);
      }
    }

    // State changes
    if (diff.editingContextChanges.length > 0) {
      lines.push('  EDITING CONTEXT CHANGES:');
      for (const change of diff.editingContextChanges.slice(0, 8)) {
        lines.push(`    - ${formatEditingContextChange(change)}`);
      }
      if (diff.editingContextChanges.length > 8) {
        lines.push(`    ... +${diff.editingContextChanges.length - 8} more editing changes`);
      }
    }

    for (const change of diff.stateChanges) {
      const fieldLabel = change.field === 'inputValue' ? 'INPUT' : change.field.toUpperCase();
      const text = change.text ? ` "${truncate(change.text, 20)}"` : '';
      lines.push(
        `  - ${fieldLabel}: ${change.role}${text} [${change.ref}]: [PREVIOUS] ${formatValue(change.before)} [CURRENT] ${formatValue(change.after)}`
      );
    }

    // New elements (limit to 5 most relevant)
    const newToShow = diff.newElements.slice(0, 5);
    if (newToShow.length > 0) {
      const moreCount = diff.newElements.length - newToShow.length;
      const moreText = moreCount > 0 ? ` (+${moreCount} more)` : '';
      lines.push(
        `  - NEW ELEMENTS: ${newToShow.map((e) => `${e.role} "${truncate(e.text, 15)}" [${e.ref}]`).join(', ')}${moreText}`
      );
    }

    // Removed elements (limit to 3)
    const removedToShow = diff.removedElements.slice(0, 3);
    if (removedToShow.length > 0) {
      const moreCount = diff.removedElements.length - removedToShow.length;
      const moreText = moreCount > 0 ? ` (+${moreCount} more)` : '';
      lines.push(
        `  - REMOVED: ${removedToShow.map((e) => `${e.role} "${truncate(e.text, 15)}" [${e.ref}]`).join(', ')}${moreText}`
      );
    }

    // Non-interactive content changes (alerts first — highest signal)
    if (diff.contentChange) {
      for (const alert of diff.contentChange.newAlerts.slice(0, 5)) {
        lines.push(`  - NEW ALERT: "${truncate(alert, 80)}"`);
      }
      const addedToShow = diff.contentChange.addedTexts
        // alerts already reported above; don't double-list them
        .filter((t) => !diff.contentChange!.newAlerts.includes(t))
        .slice(0, 5);
      if (addedToShow.length > 0) {
        const moreCount =
          diff.contentChange.addedTexts.length -
          diff.contentChange.newAlerts.length -
          addedToShow.length;
        const moreText = moreCount > 0 ? ` (+${moreCount} more)` : '';
        lines.push(`  - CONTENT: + ${addedToShow.map((t) => `"${truncate(t, 60)}"`).join(', ')}${moreText}`);
      }
      // Removed content — a cleared validation message / banner is just as
      // important as one appearing (it confirms an error/status went away).
      const removedToShow = diff.contentChange.removedTexts.slice(0, 5);
      if (removedToShow.length > 0) {
        const moreCount = diff.contentChange.removedTexts.length - removedToShow.length;
        const moreText = moreCount > 0 ? ` (+${moreCount} more)` : '';
        lines.push(
          `  - CONTENT: - ${removedToShow.map((t) => `"${truncate(t, 60)}"`).join(', ')}${moreText}`
        );
      }
    }
  }

  // Element count delta
  if (diff.elementCountDelta !== 0) {
    const sign = diff.elementCountDelta > 0 ? '+' : '';
    lines.push(`ELEMENT COUNT: ${sign}${diff.elementCountDelta}`);
  }

  // Pending network requests
  if (networkInfo && networkInfo.pendingCount > 0) {
    const reqWord = networkInfo.pendingCount === 1 ? 'request' : 'requests';
    const sampleSuffix =
      networkInfo.sampleUrls.length > 0 ? ` (e.g., ${networkInfo.sampleUrls.join(', ')})` : '';
    lines.push(`NETWORK: ${networkInfo.pendingCount} ${reqWord} still loading${sampleSuffix}`);
  }

  // No changes case
  if (!hasDiffChanges(diff)) {
    lines.push('');
    lines.push('NO CHANGES DETECTED: Page state appears unchanged.');
  }

  // Hint
  lines.push('');
  lines.push(`HINT: ${generateHint(diff, networkInfo)}`);
  lines.push('---');

  return lines.join('\n');
}

function formatEditingContextChange(change: EditingContextChange): string {
  const refText = change.ref ? ` [${change.ref}]` : '';
  const fieldText = change.fieldLabel ? `${change.fieldLabel}${refText}: ` : '';
  return `${fieldText}${change.message}`;
}

/**
 * Format a value for display (handles booleans and strings).
 */
function formatValue(value: string | boolean): string {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value === '') return '""';
  return `"${truncate(value, 20)}"`;
}
